import {
  Box,
  Container,
  Typography,
  Card,
  CardContent,
  Button,
  LinearProgress,
  Chip,
  Grid,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  CircularProgress,
  TextField,
  InputAdornment,
  Alert,
  Slider,
  CardHeader,
  Link,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import TrendingUpIcon from "@mui/icons-material/TrendingUp";
import React, { useState, useEffect, useMemo, useCallback, useRef, memo } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "react-toastify";
import Navbar from "../../components/navbar/navbar.component";
import { schemesV2Api } from "../../services/schemesV2Api";
import { generalToastStyle } from "../../utils/toast.styles";

// Configuration for installment text - Edit these values to change the text
const INSTALLMENT_CONFIG = {
  labels: {
    DAILY: "Daily installment",
    MONTHLY: "Monthly installment"
  },
  descriptions: {
    DAILY: "Amount per day",
    MONTHLY: "Amount per month"
  }
};

// Helper functions to get installment text
const getInstallmentLabel = (isDaily) => {
  return isDaily ? INSTALLMENT_CONFIG.labels.DAILY : INSTALLMENT_CONFIG.labels.MONTHLY;
};

const getInstallmentDescription = (isDaily) => {
  return isDaily ? INSTALLMENT_CONFIG.descriptions.DAILY : INSTALLMENT_CONFIG.descriptions.MONTHLY;
};

// Helper function to get terms URL
const getTermsUrl = (schemeId) => {
  switch (schemeId) {
    case "11-month-direct-purchase":
      return {
        url: "https://blogs.sadashrijewelkart.com/key-terms-11-month-direct-purchase-plan/",
        title: "11-Month Direct Purchase Plan Terms"
      };
    case "10-month-accumulation":
      return {
        url: "https://blogs.sadashrijewelkart.com/key-terms-10-month-accumulation/",
        title: "10-Month Accumulation Terms"
      };
    case "30-day-daily-savings":
      return {
        url: "https://blogs.sadashrijewelkart.com/turn-daily-savings-into-timeless-treasures/",
        title: "Daily Savings Terms"
      };
    default:
      return {
        url: "https://blogs.sadashrijewelkart.com/key-terms-10-month-accumulation/",
        title: "Terms and Conditions"
      };
  }
};

// Memoized slider component for better performance
const OptimizedSlider = memo(({ 
  value, 
  onChange, 
  min, 
  max, 
  step, 
  marks 
}) => (
  <Slider
    value={value}
    onChange={onChange}
    min={min}
    max={max}
    step={step}
    marks={marks}
    sx={{
      color: "#A36E29",
      height: 8,
      '& .MuiSlider-thumb': {
        height: 24,
        width: 24,
        backgroundColor: "#A36E29",
        border: "2px solid white",
        boxShadow: "0 2px 8px rgba(163, 110, 41, 0.3)",
        transition: "all 0.2s cubic-bezier(0.4, 0, 0.2, 1)",
        '&:hover, &.Mui-focusVisible': {
          boxShadow: "0 4px 12px rgba(163, 110, 41, 0.4)",
        },
        '&.Mui-active': {
          boxShadow: "0 4px 12px rgba(163, 110, 41, 0.4)",
        },
      },
      '& .MuiSlider-track': {
        background: "linear-gradient(to right, #d4a76a, #A36E29)",
        border: 'none',
        transition: "all 0.2s ease",
      },
      '& .MuiSlider-rail': {
        backgroundColor: "#f5f5f5",
        transition: "all 0.2s ease",
      },
      '& .MuiSlider-mark': {
        backgroundColor: "#A36E29",
        height: 8,
        width: 2,
        transition: "all 0.2s ease",
      },
      '& .MuiSlider-markLabel': {
        fontSize: "12px",
        fontFamily: '"Roboto", sans-serif',
        color: "#666",
        marginTop: "8px",
        transition: "all 0.2s ease",
      },
    }}
  />
));

const SchemesV2Simulation = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [selectedScheme, setSelectedScheme] = useState(null);
  const [schemes, setSchemes] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [simulationData, setSimulationData] = useState({
    installmentAmount: "10000",
    startDate: new Date().toISOString().split('T')[0],
  });
  const [simulationResults, setSimulationResults] = useState(null);
  const [currentRates, setCurrentRates] = useState({ gold: 5500, silver: 85 }); // Default rates
  
  // Debouncing for slider
  const debounceTimeoutRef = useRef(null);
  const [sliderValue, setSliderValue] = useState(10000);
  



  // Load schemes data
  useEffect(() => {
    // Scroll to top when component mounts
    window.scrollTo(0, 0);
    
    const fetchSchemes = async () => {
      try {
        setIsLoading(true);
        setError(null);

        let schemesData = [];
        
        try {
          // Try to use the API first
          const schemesResponse = await schemesV2Api.getAllSchemes();
          const rawSchemes = schemesResponse.data.schemes || schemesResponse.data.data?.schemes || [];
          
          // Process the schemes to convert string values to numbers
          schemesData = rawSchemes.map(scheme => ({
            ...scheme,
            monthlyInstalment: scheme.monthlyInstalment ? Number(scheme.monthlyInstalment) : null,
            dailyInstalment: scheme.dailyInstalment ? Number(scheme.dailyInstalment) : null,
            totalAmount: Number(scheme.totalAmount) || 0,
            minAmount: Number(scheme.minAmount) || 1000,
            maxAmount: Number(scheme.maxAmount) || 50000,
            gracePeriodsAfterDue: Number(scheme.gracePeriodsAfterDue) || 5
          }));
        } catch (apiError) {
          console.warn('API failed, using local data:', apiError.message);
          // Fallback to local data if API fails
          const response = await fetch('/src/data/schemesV2Data.json');
          const data = await response.json();
          schemesData = data.schemes || [];
        }
        
        setSchemes(schemesData);
        
        // Check for scheme parameter in URL
        const schemeParam = searchParams.get('scheme');
        let targetScheme = null;
        
        if (schemeParam && schemesData.length > 0) {
          // Find the scheme by ID
          targetScheme = schemesData.find(scheme => scheme.id === schemeParam);
        }
        
        // Set target scheme or first scheme as default
        const defaultScheme = targetScheme || schemesData[0];
        if (defaultScheme) {
          setSelectedScheme(defaultScheme);
          // Ensure installment amount is a valid number and start date is today
          const defaultAmount = Number(defaultScheme.monthlyInstalment || defaultScheme.dailyInstalment || 10000);
          setSimulationData(prev => ({
            ...prev,
            installmentAmount: defaultAmount.toString(),
            startDate: new Date().toISOString().split('T')[0] // Ensure it's always today
          }));
          setSliderValue(defaultAmount);
        }
      } catch (err) {
        setError(err.message);
        toast.error(`Failed to load schemes: ${err.message}`, generalToastStyle);
      } finally {
        setIsLoading(false);
      }
    };

    fetchSchemes();
    
    // Fetch current gold/silver rates
    const fetchCurrentRates = async () => {
      try {
        const ratesResponse = await schemesV2Api.getCurrentRates();
        console.log('Rates API Response:', ratesResponse);
        
        // Handle different possible response structures
        // The API returns: { data: { rates: { gold22k: 11135, silver999: 147 } } }
        let ratesObj = null;
        if (ratesResponse?.data?.rates) {
          // Nested structure: data.rates (this is the actual structure from the API)
          ratesObj = ratesResponse.data.rates;
        } else if (ratesResponse?.data) {
          // Flat structure: data
          ratesObj = ratesResponse.data;
        } else if (ratesResponse?.rates) {
          // Direct rates
          ratesObj = ratesResponse.rates;
        } else if (ratesResponse) {
          // Root level
          ratesObj = ratesResponse;
        }
        
        if (ratesObj) {
          // Try multiple possible field names for gold rate (gold22k is the actual field name)
          let goldRate = ratesObj.gold22k || 
                        ratesObj.gold_rate_22kt || 
                        ratesObj.goldRate || 
                        ratesObj.gold_22kt ||
                        ratesObj.gold_22k ||
                        ratesObj.rate_22kt ||
                        ratesObj.gold_rate ||
                        ratesObj.gold_22;
          
          // If still not found, search for any field with a value in gold rate range (5000-15000)
          if (!goldRate || goldRate === 5500) {
            for (const key in ratesObj) {
              const value = ratesObj[key];
              if (typeof value === 'number' && value >= 5000 && value <= 15000) {
                goldRate = value;
                console.log(`Found gold rate in field '${key}':`, goldRate);
                break;
              }
            }
          }
          
          // Try multiple possible field names for silver rate (silver999 is the actual field name)
          let silverRate = ratesObj.silver999 ||
                          ratesObj.silver_rate_999 || 
                          ratesObj.silverRate || 
                          ratesObj.silver_999 ||
                          ratesObj.silver_rate;
          
          // If still not found, search for any field with a value in silver rate range (50-200)
          if (!silverRate || silverRate === 85) {
            for (const key in ratesObj) {
              const value = ratesObj[key];
              if (typeof value === 'number' && value >= 50 && value <= 200) {
                silverRate = value;
                console.log(`Found silver rate in field '${key}':`, silverRate);
                break;
              }
            }
          }
          
          goldRate = Number(goldRate) || 5500;
          silverRate = Number(silverRate) || 85;
          
          console.log('Final extracted rates - Gold:', goldRate, 'Silver:', silverRate);
          
          setCurrentRates({
            gold: goldRate,
            silver: silverRate
          });
        } else {
          console.warn('No rates found in response');
        }
      } catch (error) {
        console.warn('Failed to fetch current rates, using defaults:', error.message);
        console.error('Rates fetch error:', error);
        // Keep default rates
      }
    };
    
    fetchCurrentRates();
  }, [searchParams]);

  // Generate simulation results with memoization for better performance
  const generateSimulationResults = useMemo(() => {
    if (!selectedScheme || !simulationData.installmentAmount) {
      return null;
    }

    const installmentAmount = Number(simulationData.installmentAmount) || 10000;
    const startDate = new Date(simulationData.startDate);
    
    // Calculate total periods based on scheme type
    let totalPeriods = 0;
    let isDaily = false;
    
    if (selectedScheme.dailyInstalment) {
      totalPeriods = 30; // 30-day daily savings
      isDaily = true;
    } else if (selectedScheme.monthlyInstalment) {
      // Extract number from tenure string (e.g., "11 months" -> 11)
      const tenureMatch = selectedScheme.tenure?.match(/(\d+)/);
      totalPeriods = tenureMatch ? parseInt(tenureMatch[1]) : 11;
    }

    const totalAmount = installmentAmount * totalPeriods;
    
    // Calculate completion reward
    let completionReward = 0;
    let completionRewardText = selectedScheme.completionReward || selectedScheme.completion_reward || 'No reward';
    
    // Handle different scheme types
    if (selectedScheme.id === '30-day-daily-savings') {
      // For 30-day daily savings, reward is equal to one day's installment
      completionReward = installmentAmount;
      completionRewardText = `Store Bonus equal to one day's instalment (₹${installmentAmount.toLocaleString()})`;
    } else if (selectedScheme.id === '10-month-accumulation') {
      // For 10-month accumulation, reward is 50% of one installment amount
      completionReward = installmentAmount * 0.5;
      completionRewardText = `50% bonus on your one installment's amount (₹${completionReward.toLocaleString()})`;
    } else if (selectedScheme.id === '11-month-direct-purchase') {
      // For 11-month direct purchase, reward is 100% discount on making charges
      // Assuming making charge is typically 15-20% of gold value, we'll use 15% as example
      const estimatedMakingCharge = totalAmount * 0.15;
      completionReward = estimatedMakingCharge;
      completionRewardText = `100% discount on making charges for gold jewelry, diamond jewelry, and silver articles (₹${completionReward.toLocaleString()})`;
    } else if (selectedScheme.completionReward?.includes('100%')) {
      completionReward = installmentAmount;
    } else if (selectedScheme.completionReward?.includes('50%')) {
      completionReward = installmentAmount * 0.5;
    }

    // Check if this is an accumulation scheme
    const isAccumulationScheme = selectedScheme.id === '10-month-accumulation';
    
    // For accumulation schemes, use current rate for first installment, then incremental increases
    const metalType = selectedScheme?.metal?.toLowerCase() === 'silver' ? 'silver' : 'gold';
    const currentRate = currentRates[metalType]; // Use fetched current rate

    // Generate payment schedule with dummy data
    const paymentSchedule = [];
    
    for (let i = 1; i <= totalPeriods; i++) {
      const dueDate = new Date(startDate);
      if (isDaily) {
        dueDate.setDate(dueDate.getDate() + i - 1);
      } else {
        dueDate.setMonth(dueDate.getMonth() + i - 1);
      }
      
      // Simulate payment date (usually 1-3 days after due date for simulation)
      // Use a deterministic seed based on installment number for consistent simulation
      const seed = i * 1000;
      const mockRandom = (seed) => {
        const x = Math.sin(seed) * 10000;
        return x - Math.floor(x);
      };
      const daysAfterDue = Math.floor(mockRandom(seed) * 3) + 1;
      const paymentDate = new Date(dueDate);
      paymentDate.setDate(paymentDate.getDate() + daysAfterDue);
      
      // For accumulation schemes, generate dummy gold rate and grams
      let goldRate = null;
      let goldGramsCredited = null;
      
      if (isAccumulationScheme) {
        // For first installment, use current rate; for subsequent installments, show incremental increase
        if (i === 1) {
          // Use the current/actual rate for the first installment
          goldRate = currentRate;
        } else {
          // Calculate incremental increase: ~0.5-1% per month on average
          // This creates an upward trend over time
          const monthlyIncrement = 0.0075; // 0.75% per month on average
          const cumulativeIncrease = (i - 1) * monthlyIncrement;
          
          // Add small random fluctuation (±0.2%) to make it realistic
          const smallFluctuation = (mockRandom(seed + 100) * 0.004) - 0.002; // -0.2% to +0.2%
          
          // Apply incremental increase with small fluctuation to create upward trend
          goldRate = currentRate * (1 + cumulativeIncrease + smallFluctuation);
          
          // Ensure rate doesn't go below the initial rate (safety check)
          if (goldRate < currentRate) {
            goldRate = currentRate * (1 + cumulativeIncrease);
          }
        }
        
        // Calculate gold grams for this installment
        goldGramsCredited = installmentAmount / goldRate;
      }
      
      paymentSchedule.push({
        installmentNumber: i,
        dueDate: dueDate.toLocaleDateString('en-GB'),
        paymentDate: paymentDate.toLocaleDateString('en-GB'),
        amount: installmentAmount,
        status: 'simulation',
        goldRate: goldRate,
        goldGramsCredited: goldGramsCredited
      });
    }

    // Calculate total gold grams (for accumulation schemes)
    let goldGramsAccumulated = 0;
    if (isAccumulationScheme) {
      // Use average rate for total calculation
      goldGramsAccumulated = totalAmount / currentRate;
    }

    return {
      totalAmount,
      totalPeriods,
      installmentAmount,
      completionReward,
      completionRewardText,
      paymentSchedule,
      goldGramsAccumulated,
      isDaily,
      isAccumulationScheme,
      finalValue: totalAmount + completionReward
    };
  }, [selectedScheme, simulationData, currentRates]);

  // Update simulation results when dependencies change
  useEffect(() => {
    setSimulationResults(generateSimulationResults);
  }, [generateSimulationResults]);

  const handleSchemeChange = (scheme) => {
    setSelectedScheme(scheme);
    // Reset installment amount to scheme default
    const defaultAmount = Number(scheme.monthlyInstalment || scheme.dailyInstalment || 10000);
    setSimulationData(prev => ({
      ...prev,
      installmentAmount: defaultAmount.toString()
    }));
    setSliderValue(defaultAmount);
  };

  const handleInstallmentChange = (event) => {
    const value = event.target.value;
    const numericValue = Number(value) || 10000;
    
    setSimulationData(prev => ({
      ...prev,
      installmentAmount: value
    }));
    setSliderValue(numericValue);
  };

  const handleStartDateChange = (event) => {
    setSimulationData(prev => ({
      ...prev,
      startDate: event.target.value
    }));
  };

  // Debounced slider handler for smoother performance
  const handleSliderChange = useCallback((event, newValue) => {
    const numericValue = Number(newValue) || 10000;
    setSliderValue(numericValue);
    
    // Clear existing timeout
    if (debounceTimeoutRef.current) {
      clearTimeout(debounceTimeoutRef.current);
    }
    
    // Set new timeout for debounced update
    debounceTimeoutRef.current = setTimeout(() => {
      setSimulationData(prev => ({
        ...prev,
        installmentAmount: numericValue.toString()
      }));
    }, 150); // 150ms debounce delay
  }, []);

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (debounceTimeoutRef.current) {
        clearTimeout(debounceTimeoutRef.current);
      }
    };
  }, []);

  const getStatusChip = (status) => {
    const colors = {
      simulation: { bg: "#e3f2fd", color: "#1976d2" },
      paid: { bg: "#e8f5e8", color: "#2e7d32" },
      next: { bg: "#fff3e0", color: "#f57c00" },
      pending: { bg: "#f5f5f5", color: "#616161" }
    };
    
    return (
      <Chip
        label="Simulation"
        size="small"
        sx={{
          backgroundColor: colors[status]?.bg || colors.simulation.bg,
          color: colors[status]?.color || colors.simulation.color,
          fontWeight: 600,
          fontSize: "12px"
        }}
      />
    );
  };

  // Get scheme-specific FAQs
  const getSchemeFAQs = (schemeId) => {
    const faqData = {
      '11-month-direct-purchase': [
        {
          question: "What's the quick summary?",
          answer: "Pay monthly for 11 months; on completion we waive 100% of the making/setting charge on one invoice (wastage/metal/stone/taxes still payable)."
        },
        {
          question: "Who is this best for?",
          answer: "Shoppers who want predictable savings with a clear, purchase-time making-charge benefit."
        },
        {
          question: "How does it work, step-by-step?",
          answer: "Choose a fixed monthly instalment (₹1,000+), pay for 11 months (5-day grace each month); on redemption the making/setting charge on one invoice is waived per T&Cs."
        },
        {
          question: "What's the tenure and grace?",
          answer: "11 months; 5-day grace after each due date."
        },
        {
          question: "Can I change my monthly amount later?",
          answer: "No—fixed once selected."
        },
        {
          question: "What payment modes are allowed?",
          answer: "UPI / net-banking; autopay recommended; mandates per network rules."
        },
        {
          question: "Exactly what gets waived?",
          answer: "100% of the making/setting component on one invoice; wastage, metal/stone value, hallmark/certification, duties and taxes remain payable."
        },
        {
          question: "Are \"no-making\" and \"price discount\" the same?",
          answer: "No—the waiver removes the making line; it's not a price cut on metal/stone."
        },
        {
          question: "When and how do I redeem?",
          answer: "After paying all 11 instalments; single invoice within the store-specified redemption period (e.g., 30 days)."
        },
        {
          question: "What can I buy (eligible categories)?",
          answer: "Gold (14K/18K/22K), diamond & gemstone-studded jewellery, silver articles, bespoke orders, gold coins (subject to policy)."
        },
        {
          question: "What is excluded?",
          answer: "Gift cards/credits, items above store threshold, services/repairs unless allowed."
        },
        {
          question: "Any price locks or rate promises?",
          answer: "No price locks unless offered in writing; invoice follows redemption-day rates (pre-waiver). Any \"14% making\" shown is illustrative only."
        },
        {
          question: "Can I redeem early—and what happens then?",
          answer: "Early redemption allowed but full 100% waiver needs all 11 payments; early-exit slabs may apply."
        },
        {
          question: "What are the early-exit slabs (illustrative)?",
          answer: "1–5 months: 0% making; 6–8: 25% of making; 9–11: 50% of making; full 11: 100% making waiver."
        },
        {
          question: "Can I split the waiver across invoices?",
          answer: "No—single invoice only; not encashable/transferable."
        },
        {
          question: "Can I combine this with other offers?",
          answer: "Generally yes unless a specific offer disallows; store may limit stacking."
        },
        {
          question: "Can I run multiple plans?",
          answer: "Yes; benefit applies to one invoice and can't be split; only one scheme benefit per invoice unless store policy allows otherwise."
        },
        {
          question: "Who can enrol and what KYC is needed?",
          answer: "Indian residents 18+; KYC per law/policy; PAN where required; individuals only unless expressly permitted."
        },
        {
          question: "Records, privacy and DPDP compliance?",
          answer: "Store e-records are valid; DPDP consent per Store Privacy Notice."
        },
        {
          question: "What if I miss a month?",
          answer: "Pay within the 5-day grace to stay on track; outside grace, the plan continues but you may fall into early-exit slabs."
        },
        {
          question: "Payments, limits and AML?",
          answer: "Cash transactions (if accepted) follow Section 269ST/other limits; store may decline payments breaching rules; AML/KYC compliance required."
        },
        {
          question: "Disputes and governing law?",
          answer: "Laws of India; exclusive jurisdiction as notified (e.g., Bengaluru); escalation path provided."
        },
        {
          question: "Disclaimers & liability limits?",
          answer: "Promotional purchase-assist (not deposit/investment); liability limited; illustrations are indicative only."
        }
      ],
      '30-day-daily-savings': [
        {
          question: "What's the quick summary?",
          answer: "Pay daily for 30 days; on completion you get a store bonus equal to 100% of one day's instalment, usable on one invoice."
        },
        {
          question: "Who is this best for?",
          answer: "Shoppers wanting a short, disciplined, pre-purchase plan for gems & jewellery."
        },
        {
          question: "How does it work, step-by-step?",
          answer: "Pick a daily amount (₹100–₹5,000+), pay for 30 days (≥28 required), bonus (one day's instalment) is added at completion; redeem within the window."
        },
        {
          question: "What's the tenure and minimum to qualify?",
          answer: "Tenure is 30 calendar days; qualify with at least 28 paid days."
        },
        {
          question: "How much can I pay each day? Can I change it later?",
          answer: "Any whole amount ₹100–₹5,000 or higher as permitted; once chosen, it can't be changed for that plan window."
        },
        {
          question: "What payment modes are allowed?",
          answer: "UPI / net-banking; autopay recommended. Card/UPI mandates subject to network rules."
        },
        {
          question: "What if I miss a day or autopay fails?",
          answer: "Up to two non-consecutive misses allowed if you still complete ≥28 days. Repeated autopay failures may disable the mandate and affect qualification."
        },
        {
          question: "Exactly what bonus do I get and when?",
          answer: "On 30/30 days, a Store Bonus equal to 100% of one day's instalment is credited for redemption."
        },
        {
          question: "Can I redeem early?",
          answer: "Manager-level early redemption may be allowed at Day 29; terms computed on actual days paid/top-up."
        },
        {
          question: "Where and when do I redeem?",
          answer: "Once, via a single invoice, within the 15-day redemption window after your last valid payment."
        },
        {
          question: "What can I use it on (eligible categories)?",
          answer: "Diamond & gemstone-studded gold, plain gold (14K/18K/22K), gold coins, silver articles & jewellery, ready-to-ship designs."
        },
        {
          question: "What is excluded?",
          answer: "Gift cards/credits, loose solitaires above store threshold, and services/repairs unless allowed."
        },
        {
          question: "Any price locks? How is pricing decided?",
          answer: "No price locks; the final invoice uses billing-day gold rate, making/setting, certification and taxes."
        },
        {
          question: "How are taxes/levies handled?",
          answer: "GST, hallmark/certification and other statutory levies apply; TCS/TDS collected if applicable."
        },
        {
          question: "Can I cash out the bonus or split it across invoices?",
          answer: "No cashout or split billing; bonus applies on one invoice only and unused value is forfeited."
        },
        {
          question: "What if I stop early?",
          answer: "<28 days: full principal refund to original mode, no interest/bonus, typically within 5–7 working days."
        },
        {
          question: "Can I combine this with other offers?",
          answer: "Usually yes, unless a specific promo disallows stacking; store may limit clubbing."
        },
        {
          question: "Can I run multiple plans?",
          answer: "Yes, multiple plans per mobile are permitted; only one scheme bonus per invoice."
        },
        {
          question: "Are gold coins allowed? What about gift cards?",
          answer: "Gold coins are allowed; gift cards/credits are excluded."
        },
        {
          question: "What KYC and who can enrol?",
          answer: "Indian residents 18+ with valid ID; PAN where legally required (e.g., ≥₹50,000 invoice). Individuals only unless explicitly permitted."
        },
        {
          question: "Data, records and privacy?",
          answer: "Store e-records are valid evidence; DPDP Act consent applies per Store Privacy Notice."
        },
        {
          question: "Disputes and governing law?",
          answer: "Laws of India; Bengaluru jurisdiction; escalation path includes Store Manager and Grievance Officer."
        },
        {
          question: "Any disclaimers/limits of liability?",
          answer: "It's a promotional purchase-assist (not savings/deposit); liability limited to amounts received and applicable bonus. Illustrations are indicative."
        }
      ],
      '10-month-accumulation': [
        {
          question: "What's the quick summary?",
          answer: "Pay monthly for 10 months; each payment converts to gold/silver grams at that day's rate; on completion get a bonus = 50% of one monthly instalment."
        },
        {
          question: "Who is this best for?",
          answer: "Shoppers who prefer market-linked accumulation with a clear completion bonus."
        },
        {
          question: "How does it work, step-by-step?",
          answer: "Choose monthly amount (₹1,000+), grams credited each month at store-published 22K/999 rate; at delivery your grams are valued at delivery-day rate + completion bonus added."
        },
        {
          question: "What's the tenure and due-date policy?",
          answer: "Tenure 10 months; a 5-day grace follows each monthly due date."
        },
        {
          question: "Can I change my monthly amount later?",
          answer: "No—fixed for the plan once chosen."
        },
        {
          question: "What payment modes are allowed?",
          answer: "UPI/net-banking; autopay recommended; card/UPI mandates per network rules."
        },
        {
          question: "How are grams calculated and posted?",
          answer: "Grams = instalment ÷ same-day metal rate; fractional up to three decimals; rate per store-published source."
        },
        {
          question: "What if I miss a month?",
          answer: "If unpaid beyond the grace window, no grams credited for that month."
        },
        {
          question: "What completion bonus do I get and when?",
          answer: "On 10 posted monthly payments, bonus = 50% of one monthly instalment; applied before taxes on a single invoice."
        },
        {
          question: "Can I redeem early?",
          answer: "Yes, you can redeem grams anytime before Month 10 but no completion bonus; early-exit slabs may give making-charge discounts."
        },
        {
          question: "What's the redemption window and pricing basis at delivery?",
          answer: "Redeem within 30 days after Month 10; final invoice uses delivery-day metal rate, making/setting, stones, certification and taxes."
        },
        {
          question: "What can I buy (eligible categories)?",
          answer: "Diamond & gemstone-studded gold, plain gold (14K/18K/22K), silver articles/collections, bespoke orders, gold coins (subject to policy)."
        },
        {
          question: "What is excluded?",
          answer: "Gift cards/credits, items above store threshold, services/repairs unless allowed."
        },
        {
          question: "Are there price locks? What about taxes/levies?",
          answer: "No locks; delivery-day rates and statutory taxes/levies apply."
        },
        {
          question: "Can I split redemption across invoices or carry forward bonus?",
          answer: "No—single invoice only; bonus isn't encashable/transferable/carry-forwardable beyond the window."
        },
        {
          question: "Early-exit benefit slabs (illustrative)?",
          answer: "Months 1–5: 0% making; 6–8: 15% of making; 9–10: 25% of making; full 10: 50% of one instalment as bonus."
        },
        {
          question: "Combining with other offers?",
          answer: "Generally allowed unless a specific offer disallows; store may limit stacking."
        },
        {
          question: "Can I run multiple plans?",
          answer: "Yes; each plan's bonus (if any) applies to one invoice only."
        },
        {
          question: "Who can enrol and what KYC is needed?",
          answer: "Indian residents 18+; KYC as per law/policy; PAN where legally required; individuals only unless expressly permitted."
        },
        {
          question: "Data, records and privacy?",
          answer: "Store e-records are valid; DPDP consent per Store Privacy Notice."
        },
        {
          question: "Disputes and governing law?",
          answer: "Laws of India; exclusive jurisdiction as notified (e.g., Bengaluru); escalation path defined."
        },
        {
          question: "Payment limits, AML/Compliance?",
          answer: "Cash transactions (if accepted) follow legal limits; AML/KYC compliance required; store may decline if thresholds exceeded."
        },
        {
          question: "Disclaimers & liability limits?",
          answer: "Promotional savings in metal grams (not deposit/investment); liability limited; illustrations are indicative."
        }
      ]
    };
    return faqData[schemeId] || [];
  };

  // Handle loading state
  if (isLoading) {
    return (
      <Container maxWidth="xl" disableGutters>
        <Navbar />
        <Box
          sx={{
            background: "linear-gradient(to bottom, rgb(249, 236, 220), rgb(231, 192, 125))",
            minHeight: "100vh",
            pt: { xs: 15, md: 12 },
            pb: 4,
            display: "flex",
            flexDirection: "column",
            justifyContent: "center",
            alignItems: "center"
          }}
        >
          <CircularProgress 
            size={60} 
            sx={{ color: "#A36E29", mb: 2 }} 
          />
          <Typography variant="h6" sx={{ color: "#A36E29", fontFamily: '"Roboto", sans-serif' }}>
            Loading schemes...
          </Typography>
        </Box>
      </Container>
    );
  }

  // Handle error state
  if (error) {
    return (
      <Container maxWidth="xl" disableGutters>
        <Navbar />
        <Box
          sx={{
            background: "linear-gradient(to bottom, rgb(249, 236, 220), rgb(231, 192, 125))",
            minHeight: "100vh",
            pt: { xs: 15, md: 12 },
            pb: 4,
            display: "flex",
            flexDirection: "column",
            justifyContent: "center",
            alignItems: "center"
          }}
        >
          <Typography variant="h6" sx={{ color: "#d32f2f", mb: 2 }}>
            Error loading schemes
          </Typography>
          <Typography variant="body2" sx={{ color: "#666", mb: 3 }}>
            {error}
          </Typography>
          <Button
            variant="contained"
            onClick={() => window.location.reload()}
            sx={{ backgroundColor: "#A36E29" }}
          >
            Retry
          </Button>
        </Box>
      </Container>
    );
  }

  if (!selectedScheme) return null;

  return (
    <Container maxWidth="xl" disableGutters>
      <Navbar />
      
      <Box
        sx={{
          background: "linear-gradient(to bottom, rgb(249, 236, 220), rgb(231, 192, 125))",
          minHeight: "100vh",
          pt: { xs: 25, md: 12 },
          pb: 4
        }}
      >
        {/* Header Section */}
        <Container maxWidth="lg" sx={{ mb: 4 }}>
          {/* Back Button */}
          <Box sx={{ mb: 3 }}>
            <Button
              variant="outlined"
              onClick={() => navigate('/schemes-v2?scheme=' + searchParams.get('scheme'))}
              sx={{
                color: "#A36E29",
                borderColor: "#A36E29",
                fontFamily: '"Roboto", sans-serif',
                textTransform: "none",
                borderRadius: 2,
                px: 3,
                py: 1,
                "&:hover": {
                  borderColor: "#A36E29",
                  backgroundColor: "rgba(163, 110, 41, 0.1)"
                }
              }}
            >
              ← Back to Schemes
            </Button>
          </Box>
          
          <Typography
            variant="h3"
            sx={{
              color: "#A36E29",
              fontWeight: 700,
              textAlign: "center",
              mb: 2,
              fontSize: { xs: "2rem", md: "3rem" },
              fontFamily: '"Roboto", sans-serif'
            }}
          >
            Schemes
          </Typography>
          <Typography
            variant="h6"
            sx={{
              color: "rgba(163, 110, 41, 0.8)",
              textAlign: "center",
              fontWeight: 400,
              mb: 4,
              fontFamily: '"Roboto", sans-serif'
            }}
          >
            Simulate different schemes and installment amounts to see your potential savings journey.
          </Typography>
        </Container>

        {/* Main Content Card */}
        <Container maxWidth="lg" sx={{ pb: 12 }}>
          <Card
            sx={{
              borderRadius: 3,
              boxShadow: "0 8px 32px rgba(0,0,0,0.1)",
              overflow: "hidden"
            }}
          >
            {/* Simulation Controls Header */}
            <Box sx={{ p: 3, backgroundColor: "#f8f9fa" }}>
              <Grid container spacing={3} alignItems="flex-start">
                <Grid item xs={12} md={4}>
                  <Typography variant="h6" sx={{ fontWeight: 600, mb: 2, fontFamily: '"Roboto", sans-serif' }}>
                    {searchParams.get('scheme') ? 'Selected scheme' : 'Choose scheme'}
                  </Typography>
                  <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap", alignItems: "center" }}>
                    { schemes
                      .filter(scheme => {
                        const schemeParam = searchParams.get('scheme');
                        return !schemeParam || scheme.id === schemeParam;
                      })
                      .map((scheme) => (
                      <Button
                        key={scheme.id}
                        variant={selectedScheme.id === scheme.id ? "contained" : "outlined"}
                        size="small"
                        onClick={() => handleSchemeChange(scheme)}
                        sx={{
                          borderRadius: 2,
                          textTransform: "none",
                          fontWeight: 600,
                          fontSize: "12px",
                          fontFamily: '"Roboto", sans-serif',
                          backgroundColor: selectedScheme.id === scheme.id ? "#A36E29" : "transparent",
                          color: selectedScheme.id === scheme.id ? "white" : "#A36E29",
                          borderColor: "#A36E29",
                          "&:hover": {
                            backgroundColor: selectedScheme.id === scheme.id ? "#A36E29" : "rgba(163, 110, 41, 0.04)",
                            borderColor: "#A36E29"
                          }
                        }}
                      >
                        {scheme.name}
                      </Button>
                    ))}
                    {searchParams.get('scheme') && (
                      <Button
                        variant="outlined"
                        size="small"
                        onClick={() => navigate('/schemes-v2/simulation')}
                        sx={{
                          display: "none",
                          borderRadius: 2,
                          textTransform: "none",
                          fontWeight: 600,
                          fontSize: "12px",
                          fontFamily: '"Roboto", sans-serif',
                          color: "#A36E29",
                          borderColor: "#A36E29",
                          "&:hover": {
                            backgroundColor: "rgba(163, 110, 41, 0.04)",
                            borderColor: "#A36E29"
                          }
                        }}
                      >
                        View All
                      </Button>
                    )}
                  </Box>
                </Grid>
                
                <Grid item xs={12} md={4}>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 1, fontFamily: '"Roboto", sans-serif' }}>
                    {getInstallmentLabel(selectedScheme.dailyInstalment)}
                  </Typography>
                  <TextField
                    type="text"
                    value={simulationData.installmentAmount}
                    onChange={handleInstallmentChange}
                    InputProps={{
                      startAdornment: <InputAdornment position="start">₹</InputAdornment>,
                    }}
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        fontFamily: '"Roboto", sans-serif',
                      }
                    }}
                    size="small"
                    fullWidth
                    helperText="Use slider below for easier adjustment"
                  />
                  <Typography variant="body2" color="text.secondary" sx={{ mt: 1, fontFamily: '"Roboto", sans-serif' }}>
                    {getInstallmentDescription(selectedScheme.dailyInstalment)}
                  </Typography>
                </Grid>
                
                <Grid item xs={12} md={4}>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 1, fontFamily: '"Roboto", sans-serif' }}>
                    Start date
                  </Typography>
                  <TextField
                    type="date"
                    value={simulationData.startDate}
                    onChange={handleStartDateChange}
                    inputProps={{
                      min: new Date().toISOString().split('T')[0]
                    }}
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        fontFamily: '"Roboto", sans-serif',
                      }
                    }}
                    size="small"
                    fullWidth
                  />
                </Grid>
              </Grid>

              {/* Simulation Alert */}
              <Alert 
                severity="info" 
                sx={{ mt: 2, fontFamily: '"Roboto", sans-serif' }}
                icon={<TrendingUpIcon />}
              >
                This is a simulation. Actual results may vary based on market conditions and scheme terms.
              </Alert>
            </Box>

            <CardContent sx={{ p: 0 }}>
              <Grid container>
                {/* Left Column - Simulation Results */}
                <Grid item xs={12} md={8} sx={{ p: 3 }}>
                  {/* Simulation Summary */}
                  {simulationResults && (
                    <Box sx={{ mb: 4 }}>
                      <Typography variant="h6" sx={{ fontWeight: 600, mb: 2, fontFamily: '"Roboto", sans-serif' }}>
                        Simulation Results
                      </Typography>
                      
                      <Grid container spacing={2} sx={{ mb: 3 }}>
                        <Grid item xs={6} md={3}>
                          <Typography variant="body2" color="text.secondary" sx={{ fontFamily: '"Roboto", sans-serif' }}>
                            Total amount
                          </Typography>
                          <Typography variant="h6" sx={{ fontWeight: 600, fontFamily: '"Roboto", sans-serif' }}>
                            ₹{simulationResults.totalAmount.toLocaleString()}
                          </Typography>
                        </Grid>
                        <Grid item xs={6} md={3}>
                          <Typography variant="body2" color="text.secondary" sx={{ fontFamily: '"Roboto", sans-serif' }}>
                            Duration
                          </Typography>
                          <Typography variant="h6" sx={{ fontWeight: 600, fontFamily: '"Roboto", sans-serif' }}>
                            {simulationResults.totalPeriods} {simulationResults.isDaily ? 'days' : 'months'}
                          </Typography>
                        </Grid>
                        <Grid item xs={6} md={3}>
                          <Typography variant="body2" color="text.secondary" sx={{ fontFamily: '"Roboto", sans-serif' }}>
                            Completion reward
                          </Typography>
                          <Typography variant="h6" sx={{ fontWeight: 600, color: "#A36E29", fontFamily: '"Roboto", sans-serif' }}>
                            ₹{simulationResults.completionReward.toLocaleString()}
                          </Typography>
                        </Grid>
                        <Grid item xs={6} md={3}>
                          <Typography variant="body2" color="text.secondary" sx={{ fontFamily: '"Roboto", sans-serif' }}>
                            Final value
                          </Typography>
                          <Typography variant="h6" sx={{ fontWeight: 600, color: "#2e7d32", fontFamily: '"Roboto", sans-serif' }}>
                            ₹{simulationResults.finalValue.toLocaleString()}
                          </Typography>
                        </Grid>
                      </Grid>

                      {/* Gold Grams (for accumulation schemes) */}
                      {simulationResults.goldGramsAccumulated > 0 && (
                        <Box sx={{ mb: 3, p: 2, backgroundColor: "rgba(163, 110, 41, 0.1)", borderRadius: 2 }}>
                          <Typography variant="body2" sx={{ fontWeight: 600, color: "#A36E29", fontFamily: '"Roboto", sans-serif' }}>
                            {selectedScheme?.metal?.toLowerCase() === 'silver' ? 'Silver' : 'Gold'} grams accumulated
                          </Typography>
                          <Typography variant="h6" sx={{ fontWeight: 700, color: "#A36E29", fontFamily: '"Roboto", sans-serif' }}>
                            {selectedScheme?.metal?.toLowerCase() === 'silver'
                            ? parseFloat(simulationResults.goldGramsAccumulated || 0).toFixed(2)
                            : parseFloat(simulationResults.goldGramsAccumulated || 0).toFixed(3)
                            }g ({selectedScheme.purity || '22kt'})
                          </Typography>
                          <Typography variant="body2" color="text.secondary" sx={{ fontFamily: '"Roboto", sans-serif' }}>
                            Based on current market rates
                          </Typography>
                        </Box>
                      )}

                      {/* Completion Reward Details */}
                      <Box
                        sx={{
                          mb: 3,
                          p: 2,
                          backgroundColor: "rgba(163, 110, 41, 0.1)",
                          borderRadius: 2,
                          textAlign: "center",
                          border: "1px solid rgba(163, 110, 41, 0.2)"
                        }}
                      >
                        <Typography variant="body2" sx={{ fontWeight: 600, color: "#A36E29", fontFamily: '"Roboto", sans-serif' }}>
                          Completion reward
                        </Typography>
                        <Typography variant="h6" sx={{ fontWeight: 700, color: "#A36E29", fontFamily: '"Roboto", sans-serif' }}>
                          {simulationResults.completionRewardText}
                        </Typography>
                      </Box>

                      {/* Enroll Button */}
                      <Box sx={{ textAlign: "center", mb: 3 }}>
                        <Button
                          variant="contained"
                          size="large"
                          onClick={() => {
                            // Check if user is logged in
                            const token = localStorage.getItem("token");
                            if (!token) {
                              // Store the enrollment URL to redirect back after login
                              const enrollmentUrl = `/schemes-v2/enroll/${selectedScheme.id}?amount=${simulationData.installmentAmount}&startDate=${simulationData.startDate}`;
                              localStorage.setItem("redirectAfterLogin", enrollmentUrl);
                              localStorage.setItem("redirect_to", enrollmentUrl);
                              navigate("/signin");
                              return;
                            }
                            // If logged in, proceed to enrollment
                            navigate(`/schemes-v2/enroll/${selectedScheme.id}?amount=${simulationData.installmentAmount}&startDate=${simulationData.startDate}`);
                          }}
                          sx={{
                            background: "linear-gradient(to right, #d4a76a, #A36E29)",
                            px: 4,
                            py: 1.5,
                            fontSize: "1.1rem",
                            fontWeight: 600,
                            fontFamily: '"Roboto", sans-serif',
                            borderRadius: 3,
                            textTransform: "none",
                            boxShadow: "0 4px 16px rgba(163, 110, 41, 0.3)",
                            "&:hover": {
                              background: "linear-gradient(to right, #c18a3d, #8b5a1f)",
                              boxShadow: "0 6px 20px rgba(163, 110, 41, 0.4)",
                              transform: "translateY(-2px)"
                            },
                            transition: "all 0.3s ease"
                          }}
                        >
                          Enroll in {selectedScheme.name}
                        </Button>
                        <Typography variant="body2" color="text.secondary" sx={{ mt: 1, fontFamily: '"Roboto", sans-serif' }}>
                          Ready to start your savings journey? Click to enroll with these parameters.
                        </Typography>
                        <Typography variant="body2" sx={{ mt: 2, fontFamily: '"Roboto", sans-serif', color: "#666" }}>
                          By proceeding, you agree to the{" "}
                          <Link
                            href={getTermsUrl(selectedScheme?.id).url}
                            target="_blank"
                            rel="noopener noreferrer"
                            sx={{ color: "#A36E29", textDecoration: "underline" }}
                          >
                            {getTermsUrl(selectedScheme?.id).title}
                          </Link>
                          {" and understand the terms and conditions."}
                        </Typography>
                      </Box>
                    </Box>
                  )}

                  {/* Amount Slider */}
                  {simulationResults && (
                    <Box sx={{ mb: 4 }}>
                      <Card sx={{ borderRadius: 3, boxShadow: "0 4px 16px rgba(0,0,0,0.1)" }}>
                        <CardHeader
                          title={
                            <Typography variant="h6" sx={{ fontWeight: 600, fontFamily: '"Roboto", sans-serif' }}>
                              Your Savings Journey
                            </Typography>
                          }
                          subheader={
                            <Typography variant="body2" color="text.secondary" sx={{ fontFamily: '"Roboto", sans-serif' }}>
                              Adjust the installment amount to see how much you'll save
                            </Typography>
                          }
                        />
                        <CardContent>
                          <Box sx={{ px: 2 }}>
                            <Typography variant="body2" sx={{ mb: 2, fontWeight: 600, fontFamily: '"Roboto", sans-serif' }}>
                              Monthly Installment: ₹{sliderValue.toLocaleString()}
                            </Typography>
                            
                            <OptimizedSlider
                              value={sliderValue}
                              onChange={handleSliderChange}
                              min={Number(selectedScheme.minAmount) || 1000}
                              max={Number(selectedScheme.maxAmount) || 50000}
                              step={500}
                              marks={[
                                { value: Number(selectedScheme.minAmount) || 1000, label: `₹${(Number(selectedScheme.minAmount) || 1000).toLocaleString()}` },
                                { value: Number(selectedScheme.maxAmount) || 50000, label: `₹${(Number(selectedScheme.maxAmount) || 50000).toLocaleString()}` }
                              ]}
                            />
                            
                            <Box sx={{ mt: 3, display: "flex", justifyContent: "space-between", flexWrap: "wrap", gap: 2 }}>
                              <Box sx={{ 
                                textAlign: "center", 
                                flex: 1, 
                                minWidth: "120px",
                                p: 2,
                                backgroundColor: "rgba(46, 125, 50, 0.1)",
                                borderRadius: 2,
                                border: "1px solid rgba(46, 125, 50, 0.2)"
                              }}>
                                <Typography variant="body2" color="text.secondary" sx={{ fontFamily: '"Roboto", sans-serif' }}>
                                  Total Savings
                                </Typography>
                                <Typography variant="h5" sx={{ fontWeight: 700, color: "#2e7d32", fontFamily: '"Roboto", sans-serif' }}>
                                  ₹{simulationResults.totalAmount.toLocaleString()}
                                </Typography>
                                <Typography variant="body2" sx={{ fontSize: "12px", color: "#666", fontFamily: '"Roboto", sans-serif' }}>
                                  {simulationResults.totalPeriods} × ₹{Number(simulationData.installmentAmount || 0).toLocaleString()}
                                </Typography>
                              </Box>
                              
                              <Box sx={{ 
                                textAlign: "center", 
                                flex: 1, 
                                minWidth: "120px",
                                p: 2,
                                backgroundColor: "rgba(163, 110, 41, 0.1)",
                                borderRadius: 2,
                                border: "1px solid rgba(163, 110, 41, 0.2)"
                              }}>
                                <Typography variant="body2" color="text.secondary" sx={{ fontFamily: '"Roboto", sans-serif' }}>
                                  Bonus Amount
                                </Typography>
                                <Typography variant="h5" sx={{ fontWeight: 700, color: "#A36E29", fontFamily: '"Roboto", sans-serif' }}>
                                  ₹{simulationResults.completionReward.toLocaleString()}
                                </Typography>
                                <Typography variant="body2" sx={{ fontSize: "12px", color: "#666", fontFamily: '"Roboto", sans-serif' }}>
                                  {selectedScheme.completionReward || selectedScheme.completion_reward || 'No bonus'}
                                </Typography>
                              </Box>
                              
                              <Box sx={{ 
                                textAlign: "center", 
                                flex: 1, 
                                minWidth: "120px",
                                p: 2,
                                backgroundColor: "rgba(25, 118, 210, 0.1)",
                                borderRadius: 2,
                                border: "1px solid rgba(25, 118, 210, 0.2)"
                              }}>
                                <Typography variant="body2" color="text.secondary" sx={{ fontFamily: '"Roboto", sans-serif' }}>
                                  Final Value
                                </Typography>
                                <Typography variant="h5" sx={{ fontWeight: 700, color: "#1976d2", fontFamily: '"Roboto", sans-serif' }}>
                                  ₹{simulationResults.finalValue.toLocaleString()}
                                </Typography>
                                <Typography variant="body2" sx={{ fontSize: "12px", color: "#666", fontFamily: '"Roboto", sans-serif' }}>
                                  Total + Bonus
                                </Typography>
                              </Box>
                            </Box>

                            {/* Progress Bar */}
                            <Box sx={{ mt: 3 }}>
                              <Box sx={{ display: "flex", justifyContent: "space-between", mb: 1 }}>
                                <Typography variant="body2" sx={{ fontFamily: '"Roboto", sans-serif' }}>
                                  Your Progress
                                </Typography>
                                <Typography variant="body2" sx={{ fontFamily: '"Roboto", sans-serif' }}>
                                  {simulationResults.totalPeriods} {simulationResults.isDaily ? 'days' : 'months'}
                                </Typography>
                              </Box>
                              <LinearProgress
                                variant="determinate"
                                value={100}
                                sx={{
                                  height: 8,
                                  borderRadius: 4,
                                  backgroundColor: "#f5f5f5",
                                  "& .MuiLinearProgress-bar": {
                                    background: "linear-gradient(to right, #d4a76a, #A36E29)",
                                    borderRadius: 4,
                                  }
                                }}
                              />
                              <Typography variant="body2" color="text.secondary" sx={{ mt: 1, textAlign: "center", fontFamily: '"Roboto", sans-serif' }}>
                                Complete journey simulation
                              </Typography>
                            </Box>
                          </Box>
                        </CardContent>
                      </Card>
                    </Box>
                  )}

                  {/* Payment Schedule */}
                  <Box sx={{ mb: 4 }}>
                    <Typography variant="h6" sx={{ fontWeight: 600, mb: 2, fontFamily: '"Roboto", sans-serif' }}>
                      Payment Schedule
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 2, fontFamily: '"Roboto", sans-serif' }}>
                      Simulated payment schedule for your chosen parameters
                    </Typography>
                    
                    <TableContainer component={Paper} sx={{ maxHeight: 400 }}>
                      <Table stickyHeader>
                        <TableHead>
                          <TableRow>
                            <TableCell sx={{ fontWeight: 600, fontFamily: '"Roboto", sans-serif' }}>#</TableCell>
                            <TableCell sx={{ fontWeight: 600, fontFamily: '"Roboto", sans-serif' }}>Due date</TableCell>
                            <TableCell sx={{ fontWeight: 600, fontFamily: '"Roboto", sans-serif' }}>Payment date</TableCell>
                            <TableCell sx={{ fontWeight: 600, fontFamily: '"Roboto", sans-serif' }}>Amount</TableCell>
                            <TableCell sx={{ fontWeight: 600, fontFamily: '"Roboto", sans-serif' }}>Status</TableCell>
                            
                            {simulationResults?.isAccumulationScheme && (
                              <>
                                <TableCell sx={{ fontWeight: 600, fontFamily: '"Roboto", sans-serif' }}>
                                  Accumulated {selectedScheme?.metal?.toLowerCase() === 'silver' ? 'Silver' : 'Gold'} ({selectedScheme?.purity || '22KT'})
                                </TableCell>
                                <TableCell sx={{ fontWeight: 600, fontFamily: '"Roboto", sans-serif' }}>
                                  {selectedScheme?.metal?.toLowerCase() === 'silver' ? 'Silver' : 'Gold'} Rate (On Payment Day) ({selectedScheme?.purity || '22KT'})
                                </TableCell>
                              </>
                            )}
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {simulationResults?.paymentSchedule?.map((payment) => (
                            <TableRow key={payment.installmentNumber}>
                              <TableCell sx={{ fontFamily: '"Roboto", sans-serif' }}>{payment.installmentNumber}</TableCell>
                              <TableCell sx={{ fontFamily: '"Roboto", sans-serif' }}>{payment.dueDate}</TableCell>
                              <TableCell sx={{ fontFamily: '"Roboto", sans-serif' }}>{payment.paymentDate}</TableCell>
                              <TableCell sx={{ fontFamily: '"Roboto", sans-serif' }}>₹{payment.amount.toLocaleString()}</TableCell>
                              <TableCell>
                                {getStatusChip(payment.status)}
                              </TableCell>
                              
                              {simulationResults?.isAccumulationScheme && (
                                <>
                                  <TableCell sx={{ fontFamily: '"Roboto", sans-serif', fontWeight: 600, color: '#A36E29' }}>
                                    {payment.goldGramsCredited !== null 
                                      ? (selectedScheme?.metal?.toLowerCase() === 'silver'
                                          ? parseFloat(payment.goldGramsCredited).toFixed(2)
                                          : parseFloat(payment.goldGramsCredited).toFixed(3))
                                      : '-'}g
                                  </TableCell>
                                  <TableCell sx={{ fontFamily: '"Roboto", sans-serif' }}>
                                    {payment.goldRate !== null 
                                      ? `₹${parseFloat(payment.goldRate).toFixed(2)}/g`
                                      : '-'}
                                  </TableCell>
                                </>
                              )}
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  </Box>
                </Grid>

                {/* Right Column - FAQ */}
                <Grid item xs={12} md={4} sx={{ backgroundColor: "#f8f9fa", p: 3 }}>
                  {/* FAQ */}
                  <Box sx={{ mb: 4 }}>
                    <Typography variant="h6" sx={{ fontWeight: 600, mb: 2, fontFamily: '"Roboto", sans-serif' }}>
                      Frequently Asked Questions
                    </Typography>
                    <Box sx={{ maxHeight: '600px', overflowY: 'auto' }}>
                      {(selectedScheme.faqs || getSchemeFAQs(selectedScheme?.id)).map((faq, index) => (
                          <Accordion 
                            key={index} 
                            sx={{ 
                              boxShadow: "none", 
                              "&:before": { display: "none" },
                              border: "1px solid #e0e0e0",
                              borderRadius: "8px !important",
                              mb: 1,
                              "&.Mui-expanded": {
                                margin: "0 0 8px 0"
                              }
                            }}
                          >
                            <AccordionSummary 
                              expandIcon={<ExpandMoreIcon />}
                              sx={{
                                minHeight: "48px",
                                "&.Mui-expanded": {
                                  minHeight: "48px"
                                },
                                "& .MuiAccordionSummary-content": {
                                  margin: "8px 0",
                                  "&.Mui-expanded": {
                                    margin: "8px 0"
                                  }
                                }
                              }}
                            >
                              <Typography 
                                variant="body2" 
                                sx={{ 
                                  fontWeight: 600, 
                                  fontFamily: '"Roboto", sans-serif',
                                  fontSize: "0.875rem",
                                  lineHeight: 1.4
                                }}
                              >
                              {faq.question}
                            </Typography>
                            </AccordionSummary>
                            <AccordionDetails sx={{ pt: 0, pb: 2 }}>
                              <Typography 
                                variant="body2" 
                                color="text.secondary" 
                                sx={{ 
                                  fontFamily: '"Roboto", sans-serif',
                                  fontSize: "0.8rem",
                                  lineHeight: 1.5,
                                  whiteSpace: "pre-wrap"
                                }}
                              >
                              {faq.answer}
                            </Typography>
                            </AccordionDetails>
                          </Accordion>
                      ))}
                    </Box>
                  </Box>
                </Grid>
              </Grid>
            </CardContent>
          </Card>


{/* Bottom Action Bar */}
      <Box
        sx={{
          position: "fixed",
          bottom: 0,
          left: 0,
          right: 0,
          background: "linear-gradient(to right, #d4a76a, #A36E29)",
          p: 2,
          display: { xs: "none", md: "flex" },
          justifyContent: "space-between",
          alignItems: "center",
          color: "white",
          zIndex: 1000,
          fontFamily: '"Roboto", sans-serif'
        }}
      >
        <Box>
          <Typography variant="body2" sx={{ fontFamily: '"Roboto", sans-serif' }}>Simulating</Typography>
          <Typography variant="h6" sx={{ fontWeight: 600, fontFamily: '"Roboto", sans-serif' }}>
            {selectedScheme.name}
          </Typography>
        </Box>
        
        <Typography variant="body2" sx={{ flex: 1, textAlign: "center", fontFamily: '"Roboto", sans-serif', fontSize: "14px" }}>
          {simulationResults ? 
            `Total: ₹${simulationResults.totalAmount.toLocaleString()} • Final Value: ₹${simulationResults.finalValue.toLocaleString()}` :
            'Adjust parameters to see simulation'
          }
        </Typography>

        <Box sx={{ 
          display: "flex", 
          flexDirection: "column", 
          alignItems: "flex-end" 
        }}>
          <Button
            variant="contained"
            onClick={() => {
              // Check if user is logged in
              const token = localStorage.getItem("token");
              if (!token) {
                // Store the enrollment URL to redirect back after login
                const enrollmentUrl = `/schemes-v2/enroll/${selectedScheme.id}?amount=${simulationData.installmentAmount}&startDate=${simulationData.startDate}`;
                localStorage.setItem("redirectAfterLogin", enrollmentUrl);
                localStorage.setItem("redirect_to", enrollmentUrl);
                navigate("/signin");
                return;
              }
              // If logged in, proceed to enrollment
              navigate(`/schemes-v2/enroll/${selectedScheme.id}?amount=${simulationData.installmentAmount}&startDate=${simulationData.startDate}`);
            }}
            sx={{
              backgroundColor: "white",
              color: "#A36E29",
              fontFamily: '"Roboto", sans-serif',
              fontWeight: 600,
              "&:hover": {
                backgroundColor: "rgba(255, 255, 255, 0.9)"
              }
            }}
          >
            Enroll Now
          </Button>

          <Typography variant="caption" sx={{ color: 'white', mt: 0.5, fontSize: '0.7rem' }}>
            <a 
              href="https://blogs.sadashrijewelkart.com/turn-daily-savings-into-timeless-treasures/" 
              target="_blank" 
              rel="noopener noreferrer" 
              style={{ color: 'white' }}
            >
              Terms and Conditions
            </a>
          </Typography>
        </Box>
        
      </Box>
    </Container>
  </Box>
  </Container>
  );
};

export default SchemesV2Simulation;